/**
 * @file useSkuLabelTemplates.ts
 * @description Действующий шаблон этикетки фасовки, которого не передал
 *   вызывающий — только режим «одна фасовка» (phase19.22, W17-5b).
 *
 * Строки приёмки и остатков несут `effectiveLabelTemplate` сами
 * (`SkuPrintInfo`), поэтому там хук выключен. В режиме `sku` шаблон берётся из
 * `GET /admin/wms/labels/sku/:id/lots` (право просмотра склада) с кэшем на
 * сессию вкладки: шаблон фасовки меняется редко.
 *
 * Не удалось узнать — считаем STANDARD, но шаблон в запрос не шлём: сервер
 * всё равно разрешит его сам, ошибиться может только сводка листов.
 */

import { useEffect, useState } from "react";

import { labelsApi, type LabelTemplate } from "../../../lib/api-labels";

/** Кэш «фасовка → шаблон» на время жизни вкладки. */
const templateCache = new Map<string, LabelTemplate>();

/** Сколько запросов шлём одновременно. */
const CONCURRENCY = 6;

/** Запомнить шаблон, известный из другого ответа (карточка фасовки). */
export function rememberSkuTemplate(skuId: string, template: LabelTemplate): void {
  templateCache.set(skuId, template);
}

async function loadMissing(skuIds: string[]): Promise<void> {
  for (let i = 0; i < skuIds.length; i += CONCURRENCY) {
    const chunk = skuIds.slice(i, i + CONCURRENCY);
    await Promise.all(
      chunk.map(async (skuId) => {
        try {
          const res = await labelsApi.skuLots(skuId);
          templateCache.set(skuId, res.sku.effectiveTemplate);
        } catch {
          // Сбой — остаётся STANDARD по умолчанию (см. шапку файла).
        }
      }),
    );
  }
}

interface Result {
  /** Шаблон фасовки или `undefined`, если он ещё грузится/неизвестен. */
  templateOf: (skuId: string) => LabelTemplate | undefined;
  loading: boolean;
}

/** `skuIds` — фасовки без известного шаблона; `enabled` — диалог открыт. */
export function useSkuLabelTemplates(skuIds: string[], enabled: boolean): Result {
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const key = skuIds.join(",");

  useEffect(() => {
    if (!enabled) return;
    const missing = [...new Set(key.split(",").filter(Boolean))].filter(
      (id) => !templateCache.has(id),
    );
    if (missing.length === 0) return;
    let cancelled = false;
    setLoading(true);
    void loadMissing(missing).finally(() => {
      if (cancelled) return;
      setLoading(false);
      setVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [key, enabled]);

  return {
    // `version` в замыкании не нужен: кэш общий, перерисовку даёт setVersion.
    templateOf: (skuId) => (version >= 0 ? templateCache.get(skuId) : undefined),
    loading,
  };
}
